import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  PhoneCall,
  Search,
  Table2,
  CheckCircle2,
  Clock3,
  UserRound,
  Stethoscope,
  CalendarDays,
  CalendarClock,
  X,
} from "lucide-react";
import Apiurl from "../../environment/Apiurl";
import "../../../assets/css/FollowUpCalls.css";

const STAGE_CONFIG = {
  courtesy_call: {
    title: "Courtesy Call",
    subtitle: "First patient call after prescription submission",
  },
  follow_up_1: {
    title: "Follow-up 1",
    subtitle: "First follow-up call after the courtesy call is completed",
  },
  follow_up_2: {
    title: "Follow-up 2",
    subtitle: "Final follow-up call before creating the next visit appointment",
  },
};

const FILTERS = [
  { key: "pending", label: "Pending" },
  { key: "completed", label: "Completed" },
  { key: "overdue", label: "Overdue" },
  { key: "all", label: "All" },
];

const CALL_OUTCOMES = [
  "Completed",
  "No Answer",
  "Busy",
  "Call Back Later",
  "Wrong Number",
];

const OUTCOME_FILTERS = ["All", ...CALL_OUTCOMES];

const STATUS_STYLES = {
  Completed: { backgroundColor: "#1f9254", color: "#fff" },
  Pending: { backgroundColor: "#f5a623", color: "#212529" },
  Overdue: { backgroundColor: "#d64545", color: "#fff" },
  "No Answer": { backgroundColor: "#6c757d", color: "#fff" },
  Busy: { backgroundColor: "#e8590c", color: "#fff" },
  "Call Back Later": { backgroundColor: "#2f6feb", color: "#fff" },
  "Wrong Number": { backgroundColor: "#5c2d91", color: "#fff" },
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = /^\d{4}-\d{2}-\d{2}$/.test(String(value))
    ? new Date(`${value}T00:00:00`)
    : new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getPatientName = (patient = {}) =>
  `${patient.first_name || ""} ${patient.last_name || ""}`.trim() || "N/A";

const FollowUpCalls = ({ stage }) => {
  const config = STAGE_CONFIG[stage] || STAGE_CONFIG.follow_up_1;
  const [records, setRecords] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [outcomeFilter, setOutcomeFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [showEntries, setShowEntries] = useState("10");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [callStatus, setCallStatus] = useState("Completed");
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [appointmentRecord, setAppointmentRecord] = useState(null);
  const [appointmentDate, setAppointmentDate] = useState("");
  const [appointmentTime, setAppointmentTime] = useState("");
  const [appointmentRemarks, setAppointmentRemarks] = useState("");
  const [appointmentSubmitting, setAppointmentSubmitting] = useState(false);

  const loggedInName =
    localStorage.getItem("name") ||
    localStorage.getItem("username") ||
    localStorage.getItem("user_id") ||
    "User";

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
  });

  const fetchRecords = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await axios.get(`${Apiurl}/follow-up-calls`, {
        params: {
          stage,
          status: statusFilter,
          search: searchTerm.trim(),
        },
        headers: authHeaders(),
      });
      setRecords(response.data || []);
    } catch (err) {
      console.error("Follow-up fetch error:", err);
      setRecords([]);
      setError(
        err?.response?.data?.message || "Unable to load follow-up call records"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(fetchRecords, searchTerm ? 250 : 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, statusFilter, searchTerm]);

  useEffect(() => {
    setStatusFilter("pending");
    setOutcomeFilter("All");
    setSearchTerm("");
    setCurrentPage(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [stage]);

  // Records after applying the call-outcome tab filter (client-side, on top
  // of the pending/completed/overdue/all filter that's already fetched).
  const activeStep = (record) => record?.[stage] || {};

  const outcomeFilteredRecords = useMemo(() => {
    if (outcomeFilter === "All") return records;
    return records.filter((record) => {
      const step = activeStep(record);
      return (step?.call_status || "") === outcomeFilter;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records, outcomeFilter, stage]);

  const totalPages = useMemo(() => {
    if (showEntries === "All") return 1;
    return Math.max(
      1,
      Math.ceil(outcomeFilteredRecords.length / Number(showEntries || 10))
    );
  }, [outcomeFilteredRecords.length, showEntries]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const currentItems = useMemo(() => {
    if (showEntries === "All") return outcomeFilteredRecords;
    const size = Number(showEntries || 10);
    const start = (currentPage - 1) * size;
    return outcomeFilteredRecords.slice(start, start + size);
  }, [outcomeFilteredRecords, showEntries, currentPage]);

  const canCompleteRecord = (record) => activeStep(record)?.status === "pending";

  const isAppointmentPending = (record) =>
    stage === "follow_up_2" && record?.stage === "appointment_pending";

  const openCompleteModal = (record) => {
    setSelectedRecord(record);
    setCallStatus("Completed");
    setRemarks("");
    setShowCompleteModal(true);
  };

  const closeCompleteModal = () => {
    if (submitting) return;
    setShowCompleteModal(false);
    setSelectedRecord(null);
    setCallStatus("Completed");
    setRemarks("");
  };

  const openAppointmentModal = (record) => {
    const source = record?.next_appointment || record;
    setAppointmentRecord(record);
    setAppointmentDate(source?.appointment_date || record?.next_visit_date || "");
    setAppointmentTime(source?.appointment_time || "");
    setAppointmentRemarks("");
    setShowAppointmentModal(true);
  };

  const closeAppointmentModal = () => {
    setShowAppointmentModal(false);
    setAppointmentRecord(null);
    setAppointmentDate("");
    setAppointmentTime("");
    setAppointmentRemarks("");
  };

  const handleCreateNextAppointment = async () => {
    if (!appointmentRecord?._id) return;
    if (!appointmentDate || !appointmentTime) {
      alert("Please select the next visit date and appointment time");
      return;
    }

    try {
      setAppointmentSubmitting(true);
      await axios.post(
        `${Apiurl}/follow-up-calls/${appointmentRecord._id}/create-appointment`,
        {
          appointment_date: appointmentDate,
          appointment_time: appointmentTime,
          remarks: appointmentRemarks,
        },
        { headers: authHeaders() }
      );

      closeAppointmentModal();
      await fetchRecords();
      alert("Next visit appointment created successfully");
    } catch (err) {
      console.error("Next appointment creation error:", err);
      alert(
        err?.response?.data?.message ||
          "Unable to create the next visit appointment"
      );
    } finally {
      setAppointmentSubmitting(false);
    }
  };

  const handleComplete = async () => {
    if (!selectedRecord?._id) return;
    if (!callStatus) {
      alert("Please select call status");
      return;
    }

    try {
      setSubmitting(true);
      const response = await axios.put(
        `${Apiurl}/follow-up-calls/${selectedRecord._id}/complete`,
        {
          stage,
          call_status: callStatus,
          remarks,
          completed_by_name: loggedInName,
        },
        { headers: authHeaders() }
      );

      const updatedRecord = response.data;

      setShowCompleteModal(false);
      setSelectedRecord(null);
      setCallStatus("Completed");
      setRemarks("");

      // Follow-up 2 is the last call. Immediately open the next-visit
      // appointment form with the prescription's next visit date prefilled.
      if (stage === "follow_up_2" && updatedRecord?.stage === "appointment_pending") {
        openAppointmentModal(updatedRecord);
      }

      await fetchRecords();
    } catch (err) {
      console.error("Follow-up complete error:", err);
      alert(
        err?.response?.data?.message || "Unable to complete this follow-up call"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadge = (record) => {
    const step = activeStep(record);
    if (isAppointmentPending(record)) {
      return (
        <span className="badge" style={STATUS_STYLES.Pending}>
          Appointment Pending
        </span>
      );
    }
    if (step?.status === "completed") {
      return (
        <span className="badge" style={STATUS_STYLES.Completed}>
          Completed
        </span>
      );
    }
    if (step?.status === "pending" && step?.call_status) {
      return (
        <span
          className="badge"
          style={STATUS_STYLES[step.call_status] || STATUS_STYLES.Pending}
        >
          {step.call_status}
        </span>
      );
    }
    if (record?.is_overdue) {
      return (
        <span className="badge" style={STATUS_STYLES.Overdue}>
          Overdue
        </span>
      );
    }
    return (
      <span className="badge" style={STATUS_STYLES.Pending}>
        Pending
      </span>
    );
  };

  const indexStart =
    showEntries === "All" ? 0 : (currentPage - 1) * Number(showEntries || 10);

  return (
    <div className="p-3 follow-up-page">
      <div className="follow-up-hero mb-3">
        <div className="d-flex align-items-center gap-3">
          <div className="follow-up-hero-icon">
            <PhoneCall size={22} />
          </div>
          <div>
            <h4 className="mb-1">{config.title}</h4>
            <p className="mb-0">{config.subtitle}</p>
          </div>
        </div>
        <div className="follow-up-count-card">
          <span>Current Records</span>
          <strong>{outcomeFilteredRecords.length}</strong>
        </div>
      </div>

      {stage === "courtesy_call" && (
        <div className="alert alert-info py-2 px-3 follow-up-doctor-note">
          <PhoneCall size={17} />
          <span>
            Courtesy Call is the first step after prescription submission. Any active hospital user can complete the call.
          </span>
        </div>
      )}

      {error && <div className="alert alert-danger py-2">{error}</div>}

      <div className="card card-body shadow-sm follow-up-card">
        <div className="d-flex flex-wrap align-items-center gap-2 mb-2 crm-filter-tabs">
          {FILTERS.map((filter) => (
            <button
              key={filter.key}
              type="button"
              className={`btn btn-sm ${
                statusFilter === filter.key ? "text-white" : "btn-outline-secondary"
              }`}
              style={
                statusFilter === filter.key
                  ? { backgroundColor: "#01AEBE", borderColor: "#01AEBE" }
                  : undefined
              }
              onClick={() => {
                setStatusFilter(filter.key);
                setCurrentPage(1);
              }}
            >
              {filter.label}
            </button>
          ))}
        </div>

        <div className="d-flex flex-wrap align-items-center gap-2 mb-3 crm-filter-tabs">
          <small className="text-muted me-1">Call Outcome:</small>
          {OUTCOME_FILTERS.map((outcome) => {
            const isActive = outcomeFilter === outcome;
            const activeStyle =
              outcome === "All"
                ? { backgroundColor: "#495057", borderColor: "#495057", color: "#fff" }
                : { ...STATUS_STYLES[outcome], borderColor: STATUS_STYLES[outcome]?.backgroundColor };
            return (
              <button
                key={outcome}
                type="button"
                className={`btn btn-sm ${isActive ? "" : "btn-outline-secondary"}`}
                style={isActive ? activeStyle : undefined}
                onClick={() => {
                  setOutcomeFilter(outcome);
                  setCurrentPage(1);
                }}
              >
                {outcome}
              </button>
            );
          })}
        </div>

        <div className="row mb-3 crm-table-toolbar">
          <div className="col-md-3 col-lg-2">
            <div className="input-group">
              <span className="input-group-text">
                <Table2 size={16} />
              </span>
              <select
                className="form-select"
                value={showEntries}
                onChange={(e) => {
                  setShowEntries(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="All">Show All</option>
                {[5, 10, 20, 50, 100, 200, 500, 1000].map((value) => (
                  <option key={value} value={value}>
                    Show {value}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="col-md-4 col-lg-3 ms-auto">
            <div className="position-relative">
              <input
                className="form-control pe-5"
                value={searchTerm}
                placeholder="Search patient / mobile / doctor"
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
              <Search
                size={16}
                className="position-absolute text-muted"
                style={{ right: 12, top: "50%", transform: "translateY(-50%)" }}
              />
            </div>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-hover align-middle follow-up-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Patient</th>
                <th>Mobile</th>
                <th>Consulting Doctor</th>
                <th>Prescription Date</th>
                <th>Next Visit</th>
                {stage !== "courtesy_call" && <th>Previous Follow-up</th>}
                <th>Status</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={stage === "courtesy_call" ? 8 : 9} className="text-center py-4">
                    Loading follow-up records...
                  </td>
                </tr>
              ) : currentItems.length === 0 ? (
                <tr>
                  <td colSpan={stage === "courtesy_call" ? 8 : 9} className="text-center py-4 text-muted">
                    No {statusFilter} {config.title.toLowerCase()} records found.
                  </td>
                </tr>
              ) : (
                currentItems.map((record, index) => {
                  const patient = record.patient || {};
                  const doctor = record.doctor || {};
                  const step = activeStep(record);
                  const canComplete = canCompleteRecord(record);

                  return (
                    <tr key={record._id}>
                      <td>{indexStart + index + 1}</td>
                      <td>
                        <div className="fw-semibold">{getPatientName(patient)}</div>
                        <small className="text-muted">{patient.patient_id || "—"}</small>
                      </td>
                      <td>{patient.mobile_no || "—"}</td>
                      <td>
                        <div className="fw-semibold">{doctor.doctor_name || "N/A"}</div>
                        <small className="text-muted">{doctor.doctor_id || "—"}</small>
                      </td>
                      <td>{formatDate(record.prescription_date)}</td>
                      <td>
                        <div className={record.is_overdue && step.status === "pending" ? "text-danger fw-semibold" : "fw-semibold"}>
                          {formatDate(record.next_visit_date)}
                        </div>
                      </td>

                      {stage !== "courtesy_call" && (
                        <td>
                          {stage === "follow_up_1" ? (
                            <>
                              <div className="small fw-semibold text-success">Courtesy Call Completed</div>
                              <small className="text-muted">
                                {formatDateTime(record.courtesy_call?.completed_at)}
                              </small>
                            </>
                          ) : (
                            <>
                              <div className="small fw-semibold text-success">F1 Completed</div>
                              <small className="text-muted">
                                {formatDateTime(record.follow_up_1?.completed_at)}
                              </small>
                            </>
                          )}
                        </td>
                      )}

                      <td>
                        {statusBadge(record)}
                        {step?.status === "completed" && (
                          <div className="follow-up-completed-meta mt-1">
                            <small>{step.completed_by_name || step.completed_by_user_id || "—"}</small>
                          </div>
                        )}
                      </td>
                      <td className="text-center">
                        {isAppointmentPending(record) ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-success follow-up-complete-btn"
                            onClick={() => openAppointmentModal(record)}
                          >
                            <CalendarClock size={15} />
                            Add Appointment
                          </button>
                        ) : step?.status === "pending" ? (
                          canComplete ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-success follow-up-complete-btn"
                              onClick={() => openCompleteModal(record)}
                            >
                              <CheckCircle2 size={15} />
                              Update
                            </button>
                          ) : null
                        ) : (
                          <div className="follow-up-done-cell">
                            <CheckCircle2 size={16} />
                            <span>{step.call_status || "Completed"}</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {!loading && outcomeFilteredRecords.length > 0 && (
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 pt-3">
            <small className="text-muted">
              Showing {currentItems.length} of {outcomeFilteredRecords.length} record(s)
            </small>
            {showEntries !== "All" && totalPages > 1 && (
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                >
                  Previous
                </button>
                <span className="small">
                  Page {currentPage} / {totalPages}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {showCompleteModal && selectedRecord && (
        <div className="follow-up-modal-backdrop" onMouseDown={closeCompleteModal}>
          <div className="follow-up-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="follow-up-modal-header">
              <div>
                <h5 className="mb-1">Update {config.title}</h5>
                <p className="mb-0">
                  {getPatientName(selectedRecord.patient)} · {selectedRecord.patient?.patient_id}
                </p>
              </div>
              <button type="button" className="btn btn-sm btn-light" onClick={closeCompleteModal}>
                <X size={18} />
              </button>
            </div>

            <div className="follow-up-modal-body">
              <div className="follow-up-context-grid">
                <div>
                  <UserRound size={16} />
                  <span>Patient Mobile</span>
                  <strong>{selectedRecord.patient?.mobile_no || "—"}</strong>
                </div>
                <div>
                  <Stethoscope size={16} />
                  <span>Doctor</span>
                  <strong>{selectedRecord.doctor?.doctor_name || "—"}</strong>
                </div>
                <div>
                  <CalendarDays size={16} />
                  <span>Next Visit</span>
                  <strong>{formatDate(selectedRecord.next_visit_date)}</strong>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">Call Status *</label>
                <select
                  className="form-select"
                  value={callStatus}
                  onChange={(e) => setCallStatus(e.target.value)}
                >
                  {CALL_OUTCOMES.map((outcome) => (
                    <option key={outcome} value={outcome}>
                      {outcome}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="form-label fw-semibold">Remarks</label>
                <textarea
                  className="form-control"
                  rows="4"
                  value={remarks}
                  placeholder="Enter call notes / patient response"
                  onChange={(e) => setRemarks(e.target.value)}
                />
              </div>

              <div className="follow-up-stage-info mt-3">
                {callStatus === "Completed" ? (
                  stage === "courtesy_call" ? (
                    <><Clock3 size={16} /> This will mark the call as <strong>Completed</strong> and move the patient to <strong>Follow-up 1</strong>.</>
                  ) : stage === "follow_up_1" ? (
                    <><Clock3 size={16} /> This will mark the call as <strong>Completed</strong> and move the patient to <strong>Follow-up 2</strong>.</>
                  ) : (
                    <><CalendarClock size={16} /> This will mark <strong>Follow-up 2</strong> as completed and open the <strong>Next Visit Appointment</strong> form.</>
                  )
                ) : (
                  <><Clock3 size={16} /> This will save the call outcome as <strong>{callStatus}</strong> and keep this record pending here for a retry.</>
                )}
              </div>
            </div>

            <div className="follow-up-modal-footer">
              <button type="button" className="btn btn-light" onClick={closeCompleteModal} disabled={submitting}>
                Cancel
              </button>
              <button type="button" className="btn btn-success" onClick={handleComplete} disabled={submitting}>
                {submitting ? "Saving..." : "Update"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAppointmentModal && appointmentRecord && (
        <div className="follow-up-modal-backdrop" onMouseDown={closeAppointmentModal}>
          <div className="follow-up-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="follow-up-modal-header">
              <div>
                <h5 className="mb-1">Add Next Visit Appointment</h5>
                <p className="mb-0">
                  {getPatientName(appointmentRecord.patient)} · {appointmentRecord.patient?.patient_id || "—"}
                </p>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={closeAppointmentModal}
                disabled={appointmentSubmitting}
              >
                <X size={18} />
              </button>
            </div>

            <div className="follow-up-modal-body">
              <div className="alert alert-success py-2 px-3 mb-3">
                Follow-up 2 is completed. The next visit appointment is ready to be created. The prescribed next-visit date is prefilled below, and you can change it before saving.
              </div>

              <div className="follow-up-context-grid">
                <div>
                  <UserRound size={16} />
                  <span>Patient</span>
                  <strong>{getPatientName(appointmentRecord.patient)}</strong>
                </div>
                <div>
                  <Stethoscope size={16} />
                  <span>Doctor</span>
                  <strong>{appointmentRecord.doctor?.doctor_name || "—"}</strong>
                </div>
                <div>
                  <CalendarDays size={16} />
                  <span>Suggested Next Visit</span>
                  <strong>{formatDate(appointmentRecord.next_visit_date)}</strong>
                </div>
              </div>

              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Appointment Date *</label>
                  <input
                    type="date"
                    className="form-control"
                    value={appointmentDate}
                    onChange={(e) => setAppointmentDate(e.target.value)}
                    disabled={appointmentSubmitting}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Appointment Time *</label>
                  <input
                    type="time"
                    className="form-control"
                    value={appointmentTime}
                    onChange={(e) => setAppointmentTime(e.target.value)}
                    disabled={appointmentSubmitting}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label fw-semibold">Remarks</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={appointmentRemarks}
                    placeholder="Optional appointment remarks"
                    onChange={(e) => setAppointmentRemarks(e.target.value)}
                    disabled={appointmentSubmitting}
                  />
                </div>
              </div>
            </div>

            <div className="follow-up-modal-footer">
              <button
                type="button"
                className="btn btn-light"
                onClick={closeAppointmentModal}
                disabled={appointmentSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={handleCreateNextAppointment}
                disabled={appointmentSubmitting}
              >
                {appointmentSubmitting ? "Creating..." : "Create Appointment"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FollowUpCalls;